import { api } from './api'

// Preferencias de la PERSONA, no del navegador.
//
// Lo que se guarda acá es qué columnas mira cada uno en cada grilla, con qué
// densidad y cuántas filas por página. Vivía solo en el almacenamiento del
// navegador, y eso no es «por usuario»: el dueño que acomodaba las columnas en
// la PC de la oficina las tenía de fábrica en el celular, y en la tablet del
// mostrador —un solo navegador, tres cajeros que se relevan— cada uno le
// pisaba la configuración al anterior.
//
// Cómo funciona, y el orden importa:
//
// 1. El navegador sigue teniendo su copia, POR USUARIO, y de ahí sale el
//    primer pintado. Sin eso, cada grilla aparecería con las columnas de
//    fábrica y se reacomodaría medio segundo después, que es peor que no
//    recordar nada.
// 2. Al entrar se piden al servidor y, si difieren, se avisa a las grillas
//    abiertas. El servidor es la verdad: es lo que hace que la configuración
//    te siga a cualquier dispositivo.
// 3. Cada cambio se guarda local y se manda. Si el envío falla no se muestra
//    nada: es una preferencia, no plata, y la copia local ya la recuerda en
//    este dispositivo.

let usuario: number | null = null
let valores = new Map<string, string>()

const oyentes = new Set<() => void>()

function claveLocal(usuarioId: number) {
  return `fruver.pref.${usuarioId}`
}

function leerCacheLocal(usuarioId: number): Map<string, string> {
  try {
    const raw = localStorage.getItem(claveLocal(usuarioId))
    if (!raw) return new Map()

    return new Map(Object.entries(JSON.parse(raw) as Record<string, string>))
  } catch {
    return new Map()
  }
}

function guardarCacheLocal() {
  if (usuario === null) return

  try {
    localStorage.setItem(claveLocal(usuario), JSON.stringify(Object.fromEntries(valores)))
  } catch {
    /* sin persistencia local: las preferencias viven en el servidor igual */
  }
}

/**
 * Arranca las preferencias de una persona: copia local primero, servidor
 * después.
 *
 * Se llama al entrar y en cada rehidratación, con el id del usuario de la
 * sesión. Cambiar de usuario cambia el juego entero de preferencias, que es lo
 * que hace que el relevo en la tablet no herede las del turno anterior.
 */
export async function iniciarPreferencias(usuarioId: number): Promise<void> {
  if (usuario !== usuarioId) {
    usuario = usuarioId
    valores = leerCacheLocal(usuarioId)
    avisar()
  }

  // Las preferencias son de la PERSONA y viven en el servidor (§1.1), así que
  // se piden con destino explícito a la NUBE: desde el ejecutable se abre
  // Gestión, y ahí sí hay grillas con columnas que elegir. Sin el `destino`,
  // el pedido saldría al host local mientras la pantalla es el POS y ese no
  // tiene la tabla.
  //
  // Sin conexión —o entrando con el PIN sin internet— esto falla y queda la
  // copia local, que es exactamente lo que corresponde: una preferencia de
  // presentación no puede trabar una pantalla.
  try {
    const remotas = await api.get<{ valores: Record<string, string> }>('/preferencias', {
      destino: 'nube',
    })
    const nuevas = new Map(Object.entries(remotas.valores ?? {}))

    // El servidor no tiene nada y este navegador sí: son las preferencias que
    // esta persona ya había acomodado cuando esto vivía solo en el navegador.
    // Se suben en lugar de pisarlas con la nada, porque lo contrario es
    // castigar justo a quien se tomó el trabajo de configurar sus grillas.
    if (nuevas.size === 0 && valores.size > 0) {
      for (const [clave, valor] of valores) {
        void api.put('/preferencias', { clave, valor }, { destino: 'nube' }).catch(() => {
          /* queda local; se vuelve a intentar en la próxima entrada */
        })
      }

      return
    }

    // Solo se avisa si de verdad cambió algo: una grilla que se vuelve a leer
    // sin motivo pierde el orden y la página en la que estaba.
    const igual =
      nuevas.size === valores.size && [...nuevas].every(([k, v]) => valores.get(k) === v)

    valores = nuevas
    guardarCacheLocal()

    if (!igual) avisar()
  } catch {
    // Se sigue con la copia local, sin avisar nada: una preferencia que no
    // bajó no puede impedir que la pantalla abra, ni haber un cartel de error
    // por las columnas de una grilla.
  }
}

/** Al salir: las preferencias son de quien entró, no del navegador. */
export function olvidarPreferencias() {
  usuario = null
  valores = new Map()
  avisar()
}

export function leerPreferencia<T>(clave: string, porDefecto: T): T {
  const raw = valores.get(clave)
  if (raw === undefined) return porDefecto

  try {
    return JSON.parse(raw) as T
  } catch {
    return porDefecto
  }
}

/**
 * Guarda una preferencia: local ya, servidor cuando pueda.
 *
 * `undefined` la borra, que es cómo se vuelve a la configuración de fábrica.
 */
export function guardarPreferencia(clave: string, valor: unknown): void {
  if (usuario === null) return

  const anterior = valores.get(clave)
  const nuevo = valor === undefined ? undefined : JSON.stringify(valor)

  // Nada que hacer si no cambió. Importa porque las grillas guardan en un
  // efecto que corre en cada montaje: sin este corte, abrir una pantalla
  // mandaría dos pedidos que no cambian nada.
  if (anterior === nuevo) return

  if (nuevo === undefined) valores.delete(clave)
  else valores.set(clave, nuevo)

  guardarCacheLocal()

  void api.put('/preferencias', { clave, valor: nuevo ?? null }, { destino: 'nube' }).catch(() => {
    /* la copia local ya lo recuerda; se manda de nuevo en el próximo cambio */
  })
}

/** Avisa cuando las preferencias cambiaron por afuera (llegaron del servidor). */
export function alCambiarPreferencias(oyente: () => void): () => void {
  oyentes.add(oyente)

  return () => {
    oyentes.delete(oyente)
  }
}

function avisar() {
  for (const oyente of oyentes) oyente()
}
